import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';

// Actions
import { deleteComparison, DELETE_COMPARISON, sharedWithClientComparison } from '@/redux/modules/comparison';

// Components
import ContactLabel from '../misc/ContactLabel';
import DataTableV2 from '@/views/components/data/DataTableV2/DataTableV2';
import EmptySearchResults from '@/views/components/data/DataTableV2/EmptySearchResults';
import LegacyComparisonsTable from '@/views/components/tables/LegacyComparisonsTable';
import { LoadingSpinner } from '../common';
import { withErrorBoundary } from '@/views/components/errors';
import SharedToPillLabel from './components/SharedToPillLabel';

// Modals
import { DeleteComparisonsModal, SharedWithClientModal } from '@/views/components/modals';
import { SaveComparisonModal } from '@/views/components/modals';

// Enums
import { COMPARISON_TABLE_COLUMNS_KEYS } from '@/enums/Comparison';
import { COMPARISONS_FILTER } from '@/views/containers/comparison/ComparisonHome/ComparisonsFilterNavigation';

// Hooks
import useThemeVersion from '@/hooks/useThemeVersion';

// Libraries
import { ColumnDef } from '@tanstack/react-table';

// Types
import Comparison from '@/types/comparison';
import GenericContact from '@/types/generic-contact';
import Locale from '@/types/locale';
import { MenuItem } from '../data/DataTableV2/TableMenu';
import Nullable from '@/types/nullable';
import ReducedComparison from '@/types/reduced-comparison';
import User from '@/types/user';
import { ReduxState } from '@/redux/modules/types';
import { TableClassNames } from '@/types/data-table';
import { VisibilityState } from '@tanstack/react-table';

// Utils
import { formatEventDate } from '@/utils/time';
import { formatMessage } from '@/utils/i18n';
import { getCustomColumnDef, getValueColumnDef } from '@/utils/data-table';
import { logComparisonMarkSharedWithClient } from '@/services/usage-log/usage-log-new';
import { selectIsLoadingEvery, selectIsLoadingSome } from '@/redux/modules/ui/loaders';
import { userHasPermission, PERMISSIONS, userHasPermissionEnabled } from '@/utils/permissions';

export type ComparisonTableColumnsVisibility = {
  action?: boolean;
  dateCreated?: boolean;
  lastModified?: boolean;
  name?: boolean;
  open?: boolean;
  rerun?: boolean;
  savedTo?: boolean;
};

type DispatchProps = {
  deleteComparison: (reducedComparison: ReducedComparison) => Promise<void>;
  sharedWithClientComparison: (comparison: ReducedComparison) => Promise<void>;
};

type StateProps = {
  isDeletingComparison: boolean;
  isOpeningComparison: boolean;
  locale: Locale;
  user?: Nullable<User>;
};

export type Props = DispatchProps &
  StateProps & {
    className?: string;
    comparisons: ReducedComparison[];
    comparisonSearchQuery: string;
    hasMore?: boolean;
    isContactDetailsComparisons?: boolean;
    onComparisonDeleteSuccess: (comparisons: ReducedComparison[]) => void;
    onLoadMore?: () => void;
    onOpenComparison: (comparison: ReducedComparison) => void;
    onSaveComparison: (id: string, name: string, contact?: GenericContact, isNewlyAssigned?: boolean) => Promise<Comparison>;
    selectedFilter?: COMPARISONS_FILTER;
    sortable?: boolean;
    tableColumnsVisibility?: ComparisonTableColumnsVisibility;
    user: User;
  };

type LegacyProps = {
  chevronOnHover?: boolean;
  compact?: boolean;
  fields: ComparisonsTableFields[];
  openingComparisonIds?: string[];
  rerunningComparisonIds?: string[];
  responsive?: boolean;
  scrollAncestor?: Window | HTMLElement | string;
  onDeleteComparison: (comparison: ReducedComparison) => void;
  onRerunComparison: (comparison: ReducedComparison) => void;
};

type WrapperProps = DispatchProps & StateProps & Props & LegacyProps;

const ComparisonsTableV2 = (props: Props) => {
  // Variables Declaration
  const {
    comparisons,
    comparisonSearchQuery,
    deleteComparison,
    hasMore,
    isContactDetailsComparisons = false,
    isDeletingComparison,
    isOpeningComparison,
    locale,
    onComparisonDeleteSuccess,
    onLoadMore,
    onOpenComparison,
    onSaveComparison,
    selectedFilter,
    sharedWithClientComparison,
    tableColumnsVisibility,
    user,
  } = props;

  const i18nBasePath = 'ComparisonsTableV2';
  const tableClassNames: TableClassNames = {
    isBodyHoverActive: true,
  };

  const columnVisibilityStateObj = useMemo(
    () => ({
      [COMPARISON_TABLE_COLUMNS_KEYS.ACTIONS]: tableColumnsVisibility?.action ?? true,
      [COMPARISON_TABLE_COLUMNS_KEYS.SAVED_TO]: tableColumnsVisibility?.savedTo ?? true,
      [COMPARISON_TABLE_COLUMNS_KEYS.SHARED_WITH]: comparisons.some(comparison => comparison.flags?.length > 0),
      [COMPARISON_TABLE_COLUMNS_KEYS.LAST_MODIFIED]: tableColumnsVisibility?.lastModified ?? true,
      [COMPARISON_TABLE_COLUMNS_KEYS.NAME]: tableColumnsVisibility?.name ?? true,
    }),
    [comparisons, tableColumnsVisibility],
  );

  const comparisonsData: ReducedComparison[] = useMemo(() => comparisons, [comparisons]);

  const [selectedComparison, setSelectedComparison] = useState<ReducedComparison | null>(null);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(columnVisibilityStateObj);
  const [showDeleteComparisonsModal, setShowDeleteComparisonsModal] = useState<boolean>(false);
  const [showSaveComparisonsModal, setShowSaveComparisonsModal] = useState<boolean>(false);
  const [showSharedWithClientModal, setShowSharedWithClientModal] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Set the Column Visibility state after initial render
  useEffect(() => {
    setColumnVisibility(columnVisibilityStateObj);
  }, [columnVisibilityStateObj]);

  // FUNCTIONS

  const hideDeleteComparisonsModal = () => {
    setShowDeleteComparisonsModal(false);
    setSelectedComparison(null);
  };

  const hideSharedWithClientModal = () => {
    setShowSharedWithClientModal(false);
    setSelectedComparison(null);
  };

  const onConfirmDeleteComparison = () => {
    if (!selectedComparison) return Promise.resolve();

    return deleteComparison(selectedComparison).then(() => {
      setShowDeleteComparisonsModal(false);
      setSelectedComparison(null);
      if (onComparisonDeleteSuccess) {
        onComparisonDeleteSuccess([selectedComparison]);
      }
    });
  };

  const onConfirmSharedWithClient = () => {
    if (!selectedComparison) return Promise.resolve();

    return sharedWithClientComparison(selectedComparison).then(() => {
      logComparisonMarkSharedWithClient(selectedComparison, user);
      setShowSharedWithClientModal(false);
      setSelectedComparison(null);
    });
  };

  // CELLS
  const formatLastModifiedDate = (value: string | Date): JSX.Element => <>{value ? formatEventDate(new Date(value)) : ''}</>;

  // EMPTY TABLE DATA

  const getEmptyDataComponent = (): JSX.Element => {
    const i18nTypeEmptyTable = !comparisonSearchQuery ? 'NoData' : 'NoResults';
    const i18nEmptyTablePath = `${i18nBasePath}.EmptyData.${i18nTypeEmptyTable}`;

    return (
      <EmptySearchResults
        searchQuery={comparisonSearchQuery}
        titleToLocalize={`${i18nEmptyTablePath}.Title`}
        messageToLocalize={`${i18nEmptyTablePath}.Message`}
      />
    );
  };

  // TABLE COLUMNS

  const getTableColumns: ColumnDef<ReducedComparison>[] = useMemo(
    () => [
      {
        accessorKey: COMPARISON_TABLE_COLUMNS_KEYS.NAME,
        header: () => formatMessage(`${i18nBasePath}.Headers.${COMPARISON_TABLE_COLUMNS_KEYS.NAME}`),
        cell: info => <div className="custom-name-cell">{info.getValue()}</div>,
        enableSorting: true,
      },
      getCustomColumnDef(COMPARISON_TABLE_COLUMNS_KEYS.SAVED_TO, i18nBasePath, info => <ContactLabel contact={info.row.original.contact} />),
      getCustomColumnDef(COMPARISON_TABLE_COLUMNS_KEYS.SHARED_WITH, i18nBasePath, info => <SharedToPillLabel flags={info.row.original.flags} />),
      getValueColumnDef(COMPARISON_TABLE_COLUMNS_KEYS.LAST_MODIFIED, i18nBasePath, undefined, formatLastModifiedDate),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locale],
  );

  const onClickSaveComparison = (name: string, contact?: GenericContact) => {
    const isNewlyAssigned = true;

    if (!selectedComparison?._id) {
      return;
    }

    setIsSaving(true);

    onSaveComparison(selectedComparison._id, name, contact, isNewlyAssigned)
      .then(() => {
        setShowSaveComparisonsModal(false);
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const getMenuItems = <T extends ReducedComparison>(): MenuItem<T>[] => {
    // We do not want to show the kebab menu for the Compliance filter
    if (selectedFilter === COMPARISONS_FILTER.COMPLIANCE) {
      return [];
    }

    return [
      {
        key: 'save-comparison',
        icon: 'icon-save-to-contact',
        value: formatMessage('ComparisonsTable.Menu.SaveToContact'),
        onClick: (value?: ReducedComparison) => {
          if (value) {
            setSelectedComparison(value);
          }
          return setShowSaveComparisonsModal(true);
        },
        isAllowed: (value: ReducedComparison) => !value?.contact && userHasPermission(user, PERMISSIONS.PORTFOLIOS_SAVE_TO_CONTACT),
      },
      {
        key: 'shared-with-client',
        className: 'default',
        icon: 'icon-ds-account-checked',
        value: formatMessage('ComparisonsTable.Menu.MarkSharedWithClient'),
        onClick: (value?: ReducedComparison) => {
          if (value) {
            setSelectedComparison(value);
          }
          return setShowSharedWithClientModal(true);
        },
        isAllowed: (value: ReducedComparison) =>
          userHasPermissionEnabled(user, PERMISSIONS.COMPARISON_MARK_SHARED_WITH_CLIENT) &&
          !value?.flags?.includes('SharedWithClient') &&
          isContactDetailsComparisons,
      },
      {
        key: 'delete-contact',
        className: 'destructive',
        icon: 'icon-ds-delete',
        value: formatMessage('ComparisonsTable.Menu.DeleteComparison'),
        onClick: (value?: ReducedComparison) => {
          if (value) {
            setSelectedComparison(value);
          }
          return setShowDeleteComparisonsModal(true);
        },
        isAllowed: (value: ReducedComparison) => value?.availableActions?.delete ?? false,
      },
    ];
  };

  return (
    <>
      <div className="table-container comparisons-table wide-first-column">
        {isOpeningComparison ? (
          <LoadingSpinner className="table-loading-spinner" />
        ) : (
          <DataTableV2
            columns={getTableColumns}
            columnVisibilityState={columnVisibility}
            data={comparisonsData}
            enableSorting
            menuItems={getMenuItems()}
            noDataComponent={getEmptyDataComponent}
            onRowClick={onOpenComparison}
            tableClassNames={tableClassNames}
            infiniteScrollOptions={{
              onEnter: onLoadMore,
              show: hasMore ?? false,
            }}
            tableTestId="comparisons-table"
          />
        )}
      </div>
      {showDeleteComparisonsModal && (
        <DeleteComparisonsModal
          isDeleting={isDeletingComparison}
          comparisons={selectedComparison ? [selectedComparison] : []}
          show={showDeleteComparisonsModal}
          onCancel={hideDeleteComparisonsModal}
          onConfirm={onConfirmDeleteComparison}
        />
      )}
      {showSharedWithClientModal && (
        <SharedWithClientModal showModal={showSharedWithClientModal} onCancel={hideSharedWithClientModal} onConfirm={onConfirmSharedWithClient} />
      )}
      {showSaveComparisonsModal && selectedComparison && (
        <SaveComparisonModal
          requireContact
          user={user}
          show={showSaveComparisonsModal}
          comparison={selectedComparison}
          isSaving={isSaving}
          onSave={onClickSaveComparison}
          onCancel={() => setShowSaveComparisonsModal(false)}
        />
      )}
    </>
  );
};

// TODO Deprecate: Exported Enums
export enum ComparisonsTableFields {
  Name = 'NAME',
  SavedTo = 'SAVED_TO',
  DateCreated = 'DATE_CREATED',
  LastModified = 'LAST_MODIFIED',
  Rerun = 'RERUN',
  Open = 'OPEN',
  Actions = 'ACTIONS',
}

const ComparisonsTable = (props: WrapperProps) => {
  const { isLightVersion } = useThemeVersion();
  if (isLightVersion) {
    return <ComparisonsTableV2 {...props} />;
  }
  return <LegacyComparisonsTable {...props} />;
};

const mapStateToProps = (state: ReduxState): StateProps => ({
  isDeletingComparison: selectIsLoadingEvery(state, [DELETE_COMPARISON.type]),
  isOpeningComparison: selectIsLoadingSome(state, ['OPEN_COMPARISON']),
  locale: state.intl.locale,
  user: state.auth.user,
});

export default connect(mapStateToProps, {
  deleteComparison,
  sharedWithClientComparison,
})(withErrorBoundary(ComparisonsTable));
